import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { distinctUntilChanged, finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { TeachCompensationService } from 'src/app/services/api/teach-compensation/teach-compensation.service';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import {
  CompensationSummaryParams,
  CompensationSummaryResponse,
} from 'src/app/services/models/teach-compensation/teach-compensation.model';
import { SharedModule } from 'src/shared/shared.module';

interface CompensationSummaryMetric {
  label: string;
  value: string | number;
  icon?: string;
  tone?: 'default' | 'success' | 'warning';
  unit?: string;
}

type CompensationAnnouncementType = 'regular' | 'international';

interface CompensationAnnouncementDocument {
  assetPath: string;
  title: string;
}

const COMPENSATION_ANNOUNCEMENTS: Record<CompensationAnnouncementType, CompensationAnnouncementDocument> = {
  regular: {
    assetPath: 'assets/documents/teach-compensation/regular-program-announcement.pdf',
    title: 'ตัวอย่างประกาศหลักสูตรปกติ',
  },
  international: {
    assetPath: 'assets/documents/teach-compensation/international-program-announcement.pdf',
    title: 'ตัวอย่างประกาศหลักสูตรนานาชาติ',
  },
};

interface CompensationCalculationMethod {
  summary: string;
  groups: { title: string; items: string[] }[];
  note?: string;
}

const COMPENSATION_CALCULATION_METHODS: Record<CompensationAnnouncementType, CompensationCalculationMethod> = {
  regular: {
    summary: 'คำนวณจากการสอนรายวิชาบรรยายหรือปฏิบัติการ ชั่วโมงที่สอน × สัปดาห์ที่สอน (หักลบ วันหยุด วันลา)',
    groups: [],
    note: 'ภาระงานที่นำมาคิดไม่นับรายวิชาวิทยานิพนธ์และโครงงาน Pre-Project, Project',
  },
  international: {
    summary: '18 ชม./สัปดาห์ คำนวณจากหน่วยกิตวิชาที่สอน',
    groups: [
      {
        title: 'ระดับปริญญาตรี',
        items: [
          'บรรยาย 1 หน่วยกิต = 3 ชั่วโมงภาระงาน',
          'ปฏิบัติ 1 หน่วยกิต = 4.5 ชั่วโมงภาระงาน',
        ],
      },
      {
        title: 'ระดับปริญญาโท–เอก',
        items: [
          'บรรยาย 1 หน่วยกิต = 4.5 ชั่วโมงภาระงาน',
          'ปฏิบัติ 1 หน่วยกิต = 6 ชั่วโมงภาระงาน',
        ],
      },
      {
        title: 'ที่ปรึกษาวิทยานิพนธ์',
        items: [
          'หลัก 1 คน = 2 ชั่วโมงภาระงาน',
          'ร่วม 1 คน = 1 ชั่วโมงภาระงาน',
        ],
      },
    ],
  },
};

@Component({
  selector: 'app-compensation-summary-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './compensation-summary-dashboard.component.html',
  styleUrl: './compensation-summary-dashboard.component.scss',
})
export class CompensationSummaryDashboardComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  summary = signal<CompensationSummaryResponse>(this.createEmptySummary());
  isLoading = signal(false);
  hasLoadError = signal(false);
  isAnnouncementModalOpen = signal(false);
  announcementLoading = signal(false);
  announcementError = signal(false);
  announcementPreviewUrl = signal<SafeResourceUrl | null>(null);
  selectedAnnouncement = signal<CompensationAnnouncementDocument | null>(null);
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  readonly calculationMethods = COMPENSATION_CALCULATION_METHODS;

  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private studyPeriodService = inject(StudyPeriodService);
  private teachCompensationService = inject(TeachCompensationService);
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);
  private academicTerms: AcademicTermYearModel[] = [];
  private rawAnnouncementPreviewUrl: string | null = null;
  private announcementPreviewRequestId = 0;

  get hasRegularCompensationData(): boolean {
    const summary = this.summary();

    return summary.regular_summary != null || summary.regular_amount != null;
  }

  get hasInternationalCompensationData(): boolean {
    const summary = this.summary();

    return summary.international_summary != null ||
      summary.international_amount != null ||
      summary.international_weekly_workload != null;
  }

  get currentUserName(): string {
    const user = this.currentUser()?.user;
    const fullName = user?.full_name;

    return fullName != null && fullName.trim() !== '' ? fullName : 'ผู้ใช้งานปัจจุบัน';
  }

  get currentUserEmail(): string {
    const user = this.currentUser()?.user;
    return this.displayUserValue(user?.email);
  }

  get currentUserAcademicRank(): string {
    const user = this.currentUser()?.user;
    return this.displayUserValue(user?.academic_rank);
  }

  get currentUserPosition(): string {
    const user = this.currentUser()?.user;
    return this.displayUserValue(user?.position);
  }

  get currentUserDepartments(): string[] {
    const user = this.currentUser()?.user;

    if (user == null) {
      return ['-'];
    }

    const departmentNames = (user.departments ?? [])
      .map((department) => department.name.trim())
      .filter((departmentName) => departmentName !== '');
    const uniqueDepartmentNames = Array.from(new Set(departmentNames));

    if (uniqueDepartmentNames.length > 0) {
      return uniqueDepartmentNames;
    }

    const primaryDepartment = user.department as typeof user.department | null | undefined;

    return [this.displayUserValue(primaryDepartment?.name)];
  }

  get currentUserDepartment(): string {
    return this.currentUserDepartments[0];
  }

  get academicTermLabel(): string {
    const term = this.summary().academic_term;

    if (term == null) {
      return '-';
    }

    return `${term.name}/${this.toBuddhistYear(term.year)}`;
  }

  get academicYearLabel(): string {
    const year = this.summary().academic_term?.year;

    return year == null ? '-' : String(this.toBuddhistYear(year));
  }

  get requestMetrics(): CompensationSummaryMetric[] {
    const summary = this.summary();

    return [
      { label: 'คำขอทั้งหมด', value: summary.total_request_count, icon: 'bi-file-earmark-text', unit: 'รายการ' },
      { label: 'ยังไม่อนุมัติ', value: summary.pending_request_count, icon: 'bi-clock', tone: 'warning', unit: 'รายการ' },
      { label: 'อนุมัติแล้ว', value: summary.approved_request_count, icon: 'bi-check-lg', tone: 'success', unit: 'รายการ' },
      { label: 'ชั่วโมงที่อนุมัติแล้ว', value: this.formatPlainNumber(summary.approved_hours), icon: 'bi-clock-history', tone: 'success', unit: 'ชม.' },
    ];
  }

  get totalCompensationAmount(): number {
    const summary = this.summary();

    return summary.total_amount == null
      ? this.regularCompensationAmount + this.internationalCompensationAmount
      : this.normalizeNumber(summary.total_amount);
  }

  get regularCompensationAmount(): number {
    const summary = this.summary();

    return this.firstDefinedNumber(
      summary.regular_summary?.payable_amount,
      summary.regular_amount,
    );
  }

  get undergraduateCompensationAmount(): number {
    const level = this.summary().regular_summary?.undergraduate;

    return this.firstDefinedNumber(level?.amount, level?.compensation_amount);
  }

  get graduateCompensationAmount(): number {
    const level = this.summary().regular_summary?.graduate;

    return this.firstDefinedNumber(level?.amount, level?.compensation_amount);
  }

  get regularPayableHours(): number {
    const regularSummary = this.summary().regular_summary;

    return this.normalizeNumber(regularSummary?.undergraduate?.payable_hours) +
      this.normalizeNumber(regularSummary?.graduate?.payable_hours);
  }

  get regularTotalWorkload(): number {
    const summary = this.summary();
    const undergraduateTotal = summary.regular_summary?.undergraduate?.total_hours;
    const graduateTotal = summary.regular_summary?.graduate?.total_hours;

    if (undergraduateTotal != null || graduateTotal != null) {
      return this.normalizeNumber(undergraduateTotal) + this.normalizeNumber(graduateTotal);
    }

    return this.normalizeNumber(summary.total_teaching_workload);
  }

  get regularMinimumWorkload(): number {
    return this.normalizeNumber(this.summary().minimum_teaching_workload);
  }

  get regularMeetsMinimum(): boolean {
    return this.hasRegularCompensationData &&
      this.regularTotalWorkload >= this.regularMinimumWorkload;
  }

  get regularBaseProgressPercent(): number {
    const total = this.regularTotalWorkload;
    const minimum = this.regularMinimumWorkload;

    if (minimum <= 0 || total <= 0) {
      return 0;
    }

    const scale = Math.max(total, minimum);

    return this.clampPercent((Math.min(total, minimum) / scale) * 100);
  }

  get regularExcessProgressPercent(): number {
    const total = this.regularTotalWorkload;
    const minimum = this.regularMinimumWorkload;

    if (minimum <= 0 || total <= minimum) {
      return 0;
    }

    return this.clampPercent(((total - minimum) / total) * 100);
  }

  get regularStatusLabel(): string {
    if (!this.hasRegularCompensationData) {
      return 'ไม่มีข้อมูล';
    }

    return this.regularMeetsMinimum ? 'ผ่านเกณฑ์' : 'ยังไม่ถึงเกณฑ์';
  }

  get regularStatusClass(): string {
    if (!this.hasRegularCompensationData) {
      return 'compensation-summary-dashboard-status-neutral';
    }

    return this.regularMeetsMinimum
      ? 'compensation-summary-dashboard-status-success'
      : 'compensation-summary-dashboard-status-warning';
  }

  get regularStatusIcon(): string {
    if (!this.hasRegularCompensationData) {
      return 'bi-dash-circle';
    }

    return this.regularMeetsMinimum
      ? 'bi-check-circle'
      : 'bi-exclamation-triangle';
  }

  get internationalCompensationAmount(): number {
    const summary = this.summary();

    return this.firstDefinedNumber(
      summary.international_summary?.payable_amount,
      summary.international_amount,
    );
  }

  get internationalWeeklyWorkload(): number {
    return this.normalizeNumber(
      this.summary().international_weekly_workload?.regular_weekly_hours,
    );
  }

  get internationalMinimumWeeklyWorkload(): number {
    return this.normalizeNumber(
      this.summary().international_weekly_workload?.minimum_weekly_hours,
    );
  }

  get internationalWorkloadShortfall(): number {
    return this.normalizeNumber(
      this.summary().international_weekly_workload?.workload_shortfall_hours,
    );
  }

  get internationalPayablePercentage(): number {
    return this.clampPercent(this.normalizeNumber(
      this.summary().international_weekly_workload?.payable_percentage,
    ));
  }

  get internationalProgressPercent(): number {
    const minimum = this.internationalMinimumWeeklyWorkload;

    if (minimum <= 0) {
      return 0;
    }

    return this.clampPercent((this.internationalWeeklyWorkload / minimum) * 100);
  }

  get internationalStatusLabel(): string {
    if (!this.hasInternationalCompensationData) {
      return 'ไม่มีข้อมูล';
    }

    return this.internationalWorkloadShortfall > 0 ? 'ยังไม่ถึงเกณฑ์' : 'ผ่านเกณฑ์';
  }

  get internationalStatusClass(): string {
    if (!this.hasInternationalCompensationData) {
      return 'compensation-summary-dashboard-status-neutral';
    }

    return this.internationalWorkloadShortfall > 0
      ? 'compensation-summary-dashboard-status-warning'
      : 'compensation-summary-dashboard-status-success';
  }

  get internationalStatusIcon(): string {
    if (!this.hasInternationalCompensationData) {
      return 'bi-dash-circle';
    }

    return this.internationalWorkloadShortfall > 0
      ? 'bi-exclamation-triangle'
      : 'bi-check-circle';
  }

  ngOnInit(): void {
    this.initForm();
    this.listenFilters();
    this.loadAcademicTerms();
  }

  ngOnDestroy(): void {
    this.announcementPreviewRequestId += 1;
    this.revokeAnnouncementPreviewUrl();
  }

  onOpenAnnouncementPreview(type: CompensationAnnouncementType): void {
    if (this.announcementLoading()) {
      return;
    }

    const announcement = COMPENSATION_ANNOUNCEMENTS[type];
    this.selectedAnnouncement.set(announcement);
    this.isAnnouncementModalOpen.set(true);
    this.announcementLoading.set(true);
    this.announcementError.set(false);
    this.revokeAnnouncementPreviewUrl();
    const requestId = ++this.announcementPreviewRequestId;

    this.http.get(announcement.assetPath, { responseType: 'blob' }).pipe(
      take(1),
      finalize(() => {
        if (requestId === this.announcementPreviewRequestId) {
          this.announcementLoading.set(false);
        }
      }),
    ).subscribe({
      next: (blob) => {
        if (requestId !== this.announcementPreviewRequestId || !this.isAnnouncementModalOpen()) {
          return;
        }

        this.rawAnnouncementPreviewUrl = window.URL.createObjectURL(blob);
        this.announcementPreviewUrl.set(
          this.sanitizer.bypassSecurityTrustResourceUrl(
            `${this.rawAnnouncementPreviewUrl}#toolbar=0&navpanes=0`,
          ),
        );
      },
      error: (err) => {
        if (requestId !== this.announcementPreviewRequestId || !this.isAnnouncementModalOpen()) {
          return;
        }

        console.error(err);
        this.revokeAnnouncementPreviewUrl();
        this.announcementError.set(true);
      },
    });
  }

  onCloseAnnouncementPreview(): void {
    this.announcementPreviewRequestId += 1;
    this.isAnnouncementModalOpen.set(false);
    this.announcementLoading.set(false);
    this.announcementError.set(false);
    this.revokeAnnouncementPreviewUrl();
  }

  announcementPreviewTitle(): string {
    return this.selectedAnnouncement()?.title ?? 'ตัวอย่างประกาศ';
  }

  getMetricClass(metric: CompensationSummaryMetric): string {
    return metric.tone == null
      ? 'compensation-summary-dashboard-metric-default'
      : `compensation-summary-dashboard-metric-${metric.tone}`;
  }

  formatPlainNumber(value: number | null | undefined): string {
    const numericValue = this.normalizeNumber(value);
    const formattedValue = Number.isInteger(numericValue)
      ? String(numericValue)
      : numericValue.toFixed(2).replace(/\.?0+$/, '');

    return formattedValue;
  }

  formatCurrency(value: number | null | undefined): string {
    return new Intl.NumberFormat('th-TH', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(this.normalizeNumber(value));
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = [defaultAcademicTerm];
    this.yearOptions = [defaultYear];
    this.form = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
    });
  }

  private listenFilters(): void {
    this.form.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => this.loadSummary());
  }

  private loadAcademicTerms(): void {
    this.studyPeriodService.getAcademicTermYears()
      .pipe(take(1))
      .subscribe({
        next: (terms) => {
          this.academicTerms = terms;
          this.academicTermOptions = this.buildAcademicTermOptions(terms);
          this.yearOptions = this.buildAcademicYearOptions(terms);
          this.ensureFilterDefaults();
          this.loadSummary();
        },
        error: (err) => {
          console.error(err);
          this.loadSummary();
        },
      });
  }

  private loadSummary(): void {
    this.isLoading.set(true);
    this.hasLoadError.set(false);

    this.teachCompensationService.getCompensationSummary(
      this.buildAcademicTermFilterParams(),
    ).pipe(
      finalize(() => this.isLoading.set(false)),
    ).subscribe({
      next: (summary) => {
        this.summary.set({
          total_request_count: this.normalizeNumber(summary.total_request_count),
          approved_request_count: this.normalizeNumber(summary.approved_request_count),
          pending_request_count: this.normalizeNumber(summary.pending_request_count),
          total_requested_hours: this.normalizeNumber(summary.total_requested_hours),
          approved_hours: this.normalizeNumber(summary.approved_hours),
          pending_hours: this.normalizeNumber(summary.pending_hours),
          minimum_teaching_workload: this.normalizeNumber(summary.minimum_teaching_workload),
          teaching_workload: this.normalizeNumber(summary.teaching_workload),
          makeup_teaching_workload: this.normalizeNumber(summary.makeup_teaching_workload),
          total_teaching_workload: this.normalizeNumber(summary.total_teaching_workload),
          excess_hours: this.normalizeNumber(summary.excess_hours),
          eligible_makeup_teaching_workload: this.normalizeNumber(
            summary.eligible_makeup_teaching_workload ?? summary.makeup_teaching_workload,
          ),
          calculation_total_teaching_workload: this.normalizeNumber(
            summary.calculation_total_teaching_workload ?? summary.total_teaching_workload,
          ),
          calculation_excess_hours: this.normalizeNumber(
            summary.calculation_excess_hours ?? summary.excess_hours,
          ),
          meets_minimum: summary.meets_minimum === true,
          eligible_for_disbursement: summary.eligible_for_disbursement === true,
          regular_amount: summary.regular_amount == null
            ? undefined
            : this.normalizeNumber(summary.regular_amount),
          international_amount: summary.international_amount == null
            ? undefined
            : this.normalizeNumber(summary.international_amount),
          total_amount: summary.total_amount == null
            ? undefined
            : this.normalizeNumber(summary.total_amount),
          regular_summary: summary.regular_summary ?? null,
          international_summary: summary.international_summary ?? null,
          regular_line_items: summary.regular_line_items ?? [],
          international_line_items: summary.international_line_items ?? [],
          regular_weekly_workload: summary.regular_weekly_workload,
          international_weekly_workload: summary.international_weekly_workload,
          academic_term: summary.academic_term ?? null,
          erp_comparison: summary.erp_comparison,
        });
      },
      error: (err) => {
        console.error(err);
        this.hasLoadError.set(true);
        this.summary.set(this.createEmptySummary());
      },
    });
  }

  private buildAcademicTermFilterParams(): CompensationSummaryParams {
    const academicTerm = String(this.form.get('academicTerm')?.value ?? '').trim();
    const buddhistYear = Number(this.form.get('year')?.value);
    const academicTermYear = Number.isFinite(buddhistYear)
      ? buddhistYear - 543
      : null;
    const matchedAcademicTerm = this.findAcademicTerm(academicTerm, String(buddhistYear));

    if (matchedAcademicTerm != null) {
      return { academic_term_id: matchedAcademicTerm.id };
    }

    return {
      academic_term_name: academicTerm,
      academic_term_year: academicTermYear,
    };
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    const options = Array.from(new Set(
      terms.map((term) => String(term.name).trim()).filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));

    return options.length > 0 ? options : this.academicTermOptions;
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    const options = Array.from(new Set(
      terms.map((term) => String(term.year + 543)),
    )).sort((a, b) => Number(b) - Number(a));

    return options.length > 0 ? options : this.yearOptions;
  }

  private ensureFilterDefaults(): void {
    const academicTerm = String(this.form.get('academicTerm')?.value ?? '');
    const year = String(this.form.get('year')?.value ?? '');

    if (this.findAcademicTerm(academicTerm, year) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    this.form.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private findAcademicTerm(academicTerm: string, buddhistYear: string): AcademicTermYearModel | undefined {
    const normalizedTerm = String(academicTerm).trim();
    const normalizedYear = Number(buddhistYear);

    return this.academicTerms.find((term) => (
      String(term.name).trim() === normalizedTerm &&
      this.toBuddhistYear(term.year) === normalizedYear
    ));
  }

  private getDefaultAcademicTermName(): string {
    return String(this.currentUser()?.academic_term?.name ?? '1');
  }

  private getDefaultBuddhistYear(): string {
    const currentYear = new Date().getFullYear();
    const year = this.currentUser()?.academic_term?.year ?? currentYear;

    return String(this.toBuddhistYear(year));
  }

  private toBuddhistYear(year: number): number {
    return year > 2400 ? year : year + 543;
  }

  private normalizeNumber(value: number | string | null | undefined): number {
    const numericValue = Number(value ?? 0);

    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private displayUserValue(value: string | null | undefined): string {
    const normalizedValue = value?.trim() ?? '';

    return normalizedValue === '' ? '-' : normalizedValue;
  }

  private firstDefinedNumber(
    ...values: (number | string | null | undefined)[]
  ): number {
    const value = values.find((item) => item != null && item !== '');

    return this.normalizeNumber(value);
  }

  private clampPercent(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.min(100, Math.max(0, value));
  }

  private revokeAnnouncementPreviewUrl(): void {
    if (this.rawAnnouncementPreviewUrl != null) {
      window.URL.revokeObjectURL(this.rawAnnouncementPreviewUrl);
      this.rawAnnouncementPreviewUrl = null;
    }

    this.announcementPreviewUrl.set(null);
  }

  private currentUser() {
    return this.authService.currentUser() ?? this.authService.currentUserValue ?? null;
  }

  private createEmptySummary(): CompensationSummaryResponse {
    return {
      total_request_count: 0,
      approved_request_count: 0,
      pending_request_count: 0,
      total_requested_hours: 0,
      approved_hours: 0,
      pending_hours: 0,
      minimum_teaching_workload: 0,
      teaching_workload: 0,
      makeup_teaching_workload: 0,
      total_teaching_workload: 0,
      excess_hours: 0,
      meets_minimum: false,
      eligible_for_disbursement: false,
      regular_summary: null,
      international_summary: null,
      regular_line_items: [],
      international_line_items: [],
      academic_term: null,
      erp_comparison: { has_result: false },
    };
  }
}
